import { Box, Paper, Typography } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { Alert } from '@material-ui/lab';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { CheckCircle } from '@material-ui/icons';
import { Layout } from '@roc/feature-app-core';
import { StepsContainer, VerticalBarLoader } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useStore } from 'apps/client-portal-public/src/app/hooks';
import { ReferralAuthWelcome } from './welcome';
import { BankInformation } from './bankInformation';
import { ReferralFeeSignatureStep } from './referralFeeSignatureStep';

const useStyles = makeStyles(theme =>
  createStyles({
    stepper: {
      [theme.breakpoints.down('xs')]: {
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
      },
      maxWidth: 1000,
      margin: 'auto',
      '& .MuiStepLabel-completed': {
        color: theme.palette.primary.main,
      },
    },
    box: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%'
    },
    paper: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2)
    }
  })
);

export type ReferralAuthParams = {
  token: string;
};

export const ReferralAuthPage = observer(() => {
  const { referralAuthStore } = useStore();
  const { token } = useParams<ReferralAuthParams>();
  const [showWelcome, setShowWelcome] = useState(true);

  const classes = useStyles();

  useEffect(() => {
    referralAuthStore.validateLink(token);
  }, [token]);

  const steps = [
    {
      title: 'Bank Information',
      icon: null,
      completed: referralAuthStore.activeStep > 0,
    },
    {
      title: 'Electronic Signature',
      icon: null,
      completed: referralAuthStore.isSigned,
    }
  ];

  return (
    <Layout maxWidth={'lg'}>
      <Box display={'flex'} justifyContent={'center'}>
        <Typography variant="h3">Referral Fee Authorization</Typography>
      </Box>
      {referralAuthStore.loading && <VerticalBarLoader />}
      {referralAuthStore.requestComplete && referralAuthStore.linkError && (
        <Box mt={2}>
          <Alert severity="warning">{referralAuthStore.linkError}</Alert>
        </Box>
      )}
      {referralAuthStore.requestComplete && !referralAuthStore.linkError && <Paper className={classes.paper}>
        {referralAuthStore.isSigned && <CompletedView />}
        {!referralAuthStore.isSigned && (
          <StepsContainer
            disableNavigation={true}
            activeStep={referralAuthStore.activeStep}
            handleStep={step => {
              referralAuthStore.activeStep = step;
            }}
            steps={steps}
            stepperClassName={classes.stepper}
          >
            {showWelcome ? <ReferralAuthWelcome onStart={() => setShowWelcome(false)} /> : <BankInformation />}
            <ReferralFeeSignatureStep />
          </StepsContainer>
        )}
      </Paper>}
    </Layout>
  );
});

const CompletedView = () => (
  <Box mt={4} mx={'auto'} textAlign="center">
    <CheckCircle style={{ fontSize: 60, color: green[500] }} />
    <br />
    <Typography variant="h4" color="textPrimary">
      Process Completed!
    </Typography>
    <br />
    <Typography>The referral fee authorization process has been completed.</Typography>
    <Typography>Your information was successfully received.</Typography>
    <br />
  </Box>
);
